import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addOperationsCaseNote,
  createOperationsCase,
  getOperationsCase,
  listOperationsCases,
  transitionOperationsCase,
  type OperationsCaseEvent,
  type OperationsCaseNote,
  type OperationsCaseRecord,
} from "../../lib/operationsCases";
import { FORFEO_OPERATIONS_RUNBOOKS } from "../../lib/forfeoOperationsRunbooks";

const STATUS_SUGGESTIONS = [
  "open",
  "triage",
  "triaged",
  "in_progress",
  "waiting",
  "waiting_external",
  "resolved",
  "closed",
] as const;

const CATEGORY_SUGGESTIONS = ["incident", "queue", "refund", "dispute", "recovery"] as const;
const PRIORITY_SUGGESTIONS = ["p0", "p1", "p2", "p3"] as const;

function text(value: unknown, fallback = "—") {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function caseId(record: OperationsCaseRecord | undefined | null) {
  return text(record?.id, "");
}

function timestamp(value: unknown) {
  if (typeof value !== "string" || !value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString("fr-CA");
}

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `operations-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function responseCase(payload: Record<string, unknown>) {
  const direct = payload.case;
  return direct && typeof direct === "object" && !Array.isArray(direct)
    ? (direct as OperationsCaseRecord)
    : null;
}

export default function OperationsCaseConsole() {
  const [cases, setCases] = useState<OperationsCaseRecord[]>([]);
  const [selectedCase, setSelectedCase] = useState<OperationsCaseRecord | null>(null);
  const [notes, setNotes] = useState<OperationsCaseNote[]>([]);
  const [events, setEvents] = useState<OperationsCaseEvent[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [note, setNote] = useState("");
  const [nextStatus, setNextStatus] = useState("");
  const [transitionReason, setTransitionReason] = useState("");
  const [runbookId, setRunbookId] = useState("incident-triage");
  const [createForm, setCreateForm] = useState({
    category: "incident",
    priority: "p2",
    title: "",
    summary: "",
    sourceSystem: "explore",
    sourceReference: "",
  });

  const selectedRunbook = useMemo(
    () => FORFEO_OPERATIONS_RUNBOOKS.find((item) => item.id === runbookId) || FORFEO_OPERATIONS_RUNBOOKS[0],
    [runbookId],
  );

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await listOperationsCases({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
      });
      setCases(payload.cases || []);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Dossiers indisponibles.");
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, statusFilter]);

  useEffect(() => {
    void loadCases();
  }, [loadCases]);

  async function openCase(record: OperationsCaseRecord) {
    const id = caseId(record);
    if (!id) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const payload = await getOperationsCase(id);
      setSelectedCase(payload.case || record);
      setNotes(payload.notes || []);
      setEvents(payload.events || []);
      setNextStatus("");
      setTransitionReason("");
    } catch (detailError) {
      setError(detailError instanceof Error ? detailError.message : "Détail indisponible.");
    } finally {
      setBusy(false);
    }
  }

  async function submitCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const payload = await createOperationsCase({
        idempotencyKey: newIdempotencyKey(),
        category: createForm.category.trim(),
        priority: createForm.priority.trim(),
        title: createForm.title.trim(),
        summary: createForm.summary.trim(),
        sourceSystem: createForm.sourceSystem.trim(),
        sourceReference: createForm.sourceReference.trim(),
        metadata: { initiated_from: "admin_operations", financial_mutation: false },
      });
      setNotice("Dossier enregistré. Aucun effet financier n’a été déclenché.");
      setCreateForm((current) => ({ ...current, title: "", summary: "", sourceReference: "" }));
      await loadCases();
      const created = responseCase(payload);
      if (created) await openCase(created);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Création refusée.");
    } finally {
      setBusy(false);
    }
  }

  async function submitNote(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const id = caseId(selectedCase);
    if (!id || !note.trim()) return;
    setBusy(true);
    setError("");
    try {
      await addOperationsCaseNote(id, note.trim());
      setNote("");
      setNotice("Note interne ajoutée au journal d’audit.");
      await openCase(selectedCase as OperationsCaseRecord);
    } catch (noteError) {
      setError(noteError instanceof Error ? noteError.message : "Note refusée.");
    } finally {
      setBusy(false);
    }
  }

  async function submitTransition(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const id = caseId(selectedCase);
    if (!id || !nextStatus || !transitionReason.trim()) return;
    setBusy(true);
    setError("");
    try {
      await transitionOperationsCase(id, nextStatus, transitionReason.trim());
      setNotice("Transition enregistrée dans le journal d’audit.");
      await loadCases();
      await openCase(selectedCase as OperationsCaseRecord);
    } catch (transitionError) {
      setError(transitionError instanceof Error ? transitionError.message : "Transition refusée.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-forfeo-operations-case-console className="space-y-6 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-teal-700">Lot 4C · opérations assistées</p>
          <h2 className="mt-1 text-xl font-black text-slate-950 sm:text-2xl">Dossiers opérationnels audités</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Cette console crée des dossiers, ajoute des notes et demande des transitions. Elle n’exécute aucun paiement,
            remboursement, rejeu de file ou autre mutation financière.
          </p>
        </div>
        <span className="inline-flex w-fit rounded-full bg-amber-100 px-3 py-1.5 text-xs font-black text-amber-900">
          Autorité financière : Giftshop
        </span>
      </div>

      {error ? (
        <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-800">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div role="status" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800">
          {notice}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
        <div className="space-y-4">
          <div className="flex flex-col gap-3 rounded-2xl bg-slate-50 p-4 sm:flex-row sm:items-end">
            <label className="flex-1 text-xs font-bold uppercase tracking-wide text-slate-600">
              Statut
              <input
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                list="operations-status-filter"
                placeholder="Tous"
                className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-900"
              />
            </label>
            <label className="flex-1 text-xs font-bold uppercase tracking-wide text-slate-600">
              Catégorie
              <input
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
                list="operations-category-filter"
                placeholder="Toutes"
                className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-900"
              />
            </label>
            <button
              type="button"
              onClick={() => void loadCases()}
              disabled={loading || busy}
              className="min-h-11 rounded-xl bg-slate-950 px-4 text-sm font-black text-white disabled:opacity-50"
            >
              Actualiser
            </button>
          </div>

          <datalist id="operations-status-filter">
            {STATUS_SUGGESTIONS.map((status) => <option key={status} value={status} />)}
          </datalist>
          <datalist id="operations-category-filter">
            {CATEGORY_SUGGESTIONS.map((category) => <option key={category} value={category} />)}
          </datalist>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="min-w-[760px] w-full text-left text-sm">
              <thead className="bg-slate-100 text-xs font-black uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-3">Dossier</th>
                  <th className="px-4 py-3">Catégorie</th>
                  <th className="px-4 py-3">Priorité</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Mise à jour</th>
                  <th className="px-4 py-3"><span className="sr-only">Ouvrir</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Chargement…</td></tr>
                ) : cases.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Aucun dossier pour ces filtres.</td></tr>
                ) : cases.map((record, index) => (
                  <tr key={caseId(record) || `case-${index}`} className="align-top">
                    <td className="max-w-xs px-4 py-3">
                      <p className="font-black text-slate-950">{text(record.title)}</p>
                      <p className="mt-1 break-all text-xs text-slate-500">{text(record.reference || record.id)}</p>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-700">{text(record.category)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-700">{text(record.priority)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-700">{text(record.status)}</td>
                    <td className="px-4 py-3 text-slate-600">{timestamp(record.updated_at || record.created_at)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void openCase(record)}
                        className="min-h-10 rounded-lg border border-slate-300 px-3 font-black text-slate-800 hover:bg-slate-50"
                      >
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <form onSubmit={submitCreate} data-forfeo-operations-case-create className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div>
            <h3 className="text-lg font-black text-slate-950">Nouveau dossier</h3>
            <p className="mt-1 text-xs leading-5 text-slate-600">La création est idempotente et produit une trace d’audit.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Catégorie
              <input required list="operations-category-filter" value={createForm.category} onChange={(event) => setCreateForm({ ...createForm, category: event.target.value })} className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm normal-case text-slate-900" />
            </label>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Priorité
              <select required value={createForm.priority} onChange={(event) => setCreateForm({ ...createForm, priority: event.target.value })} className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm normal-case text-slate-900">
                {PRIORITY_SUGGESTIONS.map((priority) => <option key={priority} value={priority}>{priority.toUpperCase()}</option>)}
              </select>
            </label>
          </div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-600">
            Titre
            <input required minLength={4} maxLength={160} value={createForm.title} onChange={(event) => setCreateForm({ ...createForm, title: event.target.value })} className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm normal-case text-slate-900" />
          </label>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-600">
            Résumé factuel
            <textarea required minLength={8} maxLength={4000} rows={4} value={createForm.summary} onChange={(event) => setCreateForm({ ...createForm, summary: event.target.value })} className="mt-1 w-full rounded-xl border border-slate-300 bg-white p-3 text-sm normal-case text-slate-900" />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Système source
              <select value={createForm.sourceSystem} onChange={(event) => setCreateForm({ ...createForm, sourceSystem: event.target.value })} className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm normal-case text-slate-900">
                <option value="explore">Explore</option>
                <option value="giftshop">Giftshop</option>
              </select>
            </label>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Référence source
              <input required maxLength={200} value={createForm.sourceReference} onChange={(event) => setCreateForm({ ...createForm, sourceReference: event.target.value })} className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm normal-case text-slate-900" />
            </label>
          </div>
          <button type="submit" disabled={busy} className="min-h-11 w-full rounded-xl bg-teal-700 px-4 text-sm font-black text-white disabled:opacity-50">
            Créer le dossier sans effet financier
          </button>
        </form>
      </div>

      {selectedCase ? (
        <div data-forfeo-operations-case-detail className="grid gap-6 rounded-2xl border border-slate-200 p-4 lg:grid-cols-2">
          <div className="space-y-4">
            <div>
              <p className="text-xs font-black uppercase tracking-wide text-slate-500">Dossier sélectionné</p>
              <h3 className="mt-1 text-lg font-black text-slate-950">{text(selectedCase.title)}</h3>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{text(selectedCase.summary || selectedCase.description)}</p>
            </div>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-slate-50 p-3"><dt className="text-xs font-bold uppercase text-slate-500">Statut</dt><dd className="mt-1 font-black text-slate-900">{text(selectedCase.status)}</dd></div>
              <div className="rounded-xl bg-slate-50 p-3"><dt className="text-xs font-bold uppercase text-slate-500">Priorité</dt><dd className="mt-1 font-black text-slate-900">{text(selectedCase.priority)}</dd></div>
              <div className="rounded-xl bg-slate-50 p-3"><dt className="text-xs font-bold uppercase text-slate-500">Source</dt><dd className="mt-1 break-all font-black text-slate-900">{text(selectedCase.source_system || selectedCase.source_repository)}</dd></div>
              <div className="rounded-xl bg-slate-50 p-3"><dt className="text-xs font-bold uppercase text-slate-500">Référence</dt><dd className="mt-1 break-all font-black text-slate-900">{text(selectedCase.source_reference || selectedCase.source_ref || selectedCase.source_id)}</dd></div>
            </dl>

            <form onSubmit={submitTransition} data-forfeo-operations-case-transition className="space-y-3 rounded-2xl bg-slate-50 p-4">
              <h4 className="font-black text-slate-950">Transition auditée</h4>
              <input required list="operations-status-filter" value={nextStatus} onChange={(event) => setNextStatus(event.target.value)} placeholder="Prochain statut" className="min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" />
              <textarea required minLength={4} maxLength={2000} rows={3} value={transitionReason} onChange={(event) => setTransitionReason(event.target.value)} placeholder="Motif factuel" className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm text-slate-900" />
              <button type="submit" disabled={busy} className="min-h-11 rounded-xl bg-slate-950 px-4 text-sm font-black text-white disabled:opacity-50">Demander la transition</button>
            </form>
          </div>

          <div className="space-y-4">
            <form onSubmit={submitNote} data-forfeo-operations-case-note className="space-y-3 rounded-2xl bg-slate-50 p-4">
              <h4 className="font-black text-slate-950">Note interne</h4>
              <textarea required minLength={2} maxLength={4000} rows={4} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Faits, décision ou résultat de vérification — sans secret ni donnée de carte" className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm text-slate-900" />
              <button type="submit" disabled={busy} className="min-h-11 rounded-xl bg-teal-700 px-4 text-sm font-black text-white disabled:opacity-50">Ajouter au journal</button>
            </form>
            <div className="rounded-2xl border border-slate-200 p-4">
              <h4 className="font-black text-slate-950">Chronologie</h4>
              <div className="mt-3 max-h-80 space-y-3 overflow-y-auto pr-1">
                {notes.length === 0 && events.length === 0 ? <p className="text-sm text-slate-500">Aucune entrée.</p> : null}
                {notes.map((entry, index) => (
                  <article key={String(entry.id || `note-${index}`)} className="rounded-xl bg-slate-50 p-3 text-sm">
                    <p className="whitespace-pre-wrap font-medium text-slate-800">{text(entry.body || entry.note)}</p>
                    <p className="mt-2 text-xs text-slate-500">{text(entry.author_email)} · {timestamp(entry.created_at)}</p>
                  </article>
                ))}
                {events.map((entry, index) => (
                  <article key={String(entry.id || `event-${index}`)} className="rounded-xl border border-slate-200 p-3 text-sm">
                    <p className="font-black text-slate-800">{text(entry.event_type, "Événement")}</p>
                    <p className="mt-1 text-xs text-slate-500">{text(entry.actor_email)} · {timestamp(entry.created_at)}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div data-forfeo-operations-runbooks className="rounded-2xl border border-slate-200 bg-slate-950 p-4 text-white sm:p-6">
        <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
          <div>
            <label className="text-xs font-black uppercase tracking-wide text-slate-300">
              Procédure
              <select value={runbookId} onChange={(event) => setRunbookId(event.target.value)} className="mt-2 min-h-11 w-full rounded-xl border border-slate-600 bg-slate-900 px-3 text-sm normal-case text-white">
                {FORFEO_OPERATIONS_RUNBOOKS.map((runbook) => <option key={runbook.id} value={runbook.id}>{runbook.title}</option>)}
              </select>
            </label>
            <p className="mt-4 text-xs leading-5 text-slate-300">Propriétaire : <strong className="text-white">{selectedRunbook.owner}</strong></p>
            <p className="mt-1 text-xs leading-5 text-slate-300">Domaine : <strong className="text-white">{selectedRunbook.domain}</strong></p>
          </div>
          <div>
            <h3 className="text-lg font-black">{selectedRunbook.title}</h3>
            <p className="mt-2 text-sm leading-6 text-slate-300">{selectedRunbook.objective}</p>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-6 text-slate-200">
              {selectedRunbook.steps.map((step) => <li key={step}>{step}</li>)}
            </ol>
            <div className="mt-5 rounded-xl border border-amber-400/40 bg-amber-400/10 p-4">
              <p className="text-xs font-black uppercase tracking-wide text-amber-200">Limite financière</p>
              <p className="mt-1 text-sm leading-6 text-amber-50">{selectedRunbook.financialBoundary}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
